import { useState } from 'react';
import { Search, X, Download, CheckCircle2, AlertCircle, Loader2, Camera, User, Calendar } from 'lucide-react';
import { onboardingApi } from '../lib/api';

function formatDateIT(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return iso;
    }
}

export default function ImportFromOnboardingDialog({ isOpen, caseId, caseHasInvestigatedData, onClose, onImported }) {
    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [searchError, setSearchError] = useState('');
    const [searched, setSearched] = useState(false);

    const [selected, setSelected] = useState(null); // risultato selezionato
    const [overwrite, setOverwrite] = useState(false);
    const [includePhotos, setIncludePhotos] = useState(true);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState('');

    if (!isOpen) return null;

    const handleClose = () => {
        if (importing) return;
        setQuery('');
        setResults([]);
        setSelected(null);
        setSearched(false);
        setSearchError('');
        setImportError('');
        setImportResult(null);
        setOverwrite(false);
        setIncludePhotos(true);
        onClose();
    };

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        const trimmed = query.trim();
        if (!trimmed) return;
        setSearching(true);
        setSearchError('');
        setSearched(true);
        setResults([]);
        setSelected(null);
        setImportResult(null);
        setImportError('');
        try {
            const res = await onboardingApi.search(trimmed);
            setResults(res.data?.results || []);
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || 'Errore durante la ricerca';
            setSearchError(msg);
        } finally {
            setSearching(false);
        }
    };

    const handleImport = async () => {
        if (!selected) return;
        setImporting(true);
        setImportError('');
        setImportResult(null);
        try {
            const res = await onboardingApi.importToCase(caseId, selected.id, {
                overwrite,
                includePhotos,
            });
            setImportResult(res.data);
            if (onImported) await onImported();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || 'Errore durante l\'import';
            setImportError(msg);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            data-testid="import-onboarding-dialog"
        >
            <div className="bg-[#0A0A0A] border border-[#00FF41]/30 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41]/30 flex items-center justify-center">
                            <Download className="w-5 h-5 text-[#00FF41]" />
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-lg font-heading">Importa da Smart Onboarding</h3>
                            <p className="text-white/50 text-xs">Cerca una scheda indagato e importa i dati nel caso</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={importing}
                        className="text-white/50 hover:text-white disabled:opacity-40"
                        data-testid="import-onboarding-close-btn"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 p-5 space-y-4">
                    {/* Ricerca */}
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Cerca per nome indagato..."
                                className="w-full pl-10 pr-3 py-2.5 bg-black border border-white/20 text-white text-sm focus:border-[#00FF41]/50 focus:outline-none"
                                disabled={searching || importing}
                                data-testid="import-onboarding-search-input"
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={searching || importing || !query.trim()}
                            className="px-4 py-2.5 bg-[#00FF41] text-black text-sm font-mono uppercase hover:bg-[#00FF41]/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            data-testid="import-onboarding-search-btn"
                        >
                            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            Cerca
                        </button>
                    </form>

                    {searchError && (
                        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="import-onboarding-search-error">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{searchError}</span>
                        </div>
                    )}

                    {/* Risultati */}
                    {!importResult && searched && !searching && !searchError && (
                        <>
                            {results.length === 0 ? (
                                <div className="text-center py-8 text-white/50 text-sm" data-testid="import-onboarding-no-results">
                                    Nessuna scheda trovata per "{query}"
                                </div>
                            ) : (
                                <div className="space-y-2" data-testid="import-onboarding-results">
                                    <p className="text-white/50 text-xs uppercase font-mono">
                                        {results.length} {results.length === 1 ? 'risultato' : 'risultati'}
                                    </p>
                                    {results.map((r) => {
                                        const isSelected = selected?.id === r.id;
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                onClick={() => setSelected(r)}
                                                className={`w-full text-left p-3 border transition-colors ${
                                                    isSelected
                                                        ? 'border-[#00FF41] bg-[#00FF41]/10'
                                                        : 'border-white/10 hover:border-white/30 bg-black/40'
                                                }`}
                                                data-testid={`import-onboarding-result-${r.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <User className="w-4 h-4 text-[#00FF41] flex-shrink-0" />
                                                            <span className="text-white font-medium capitalize truncate">
                                                                {r.investigated_name || '—'}
                                                            </span>
                                                            {r.has_photos && (
                                                                <span className="flex items-center gap-1 text-xs text-[#00FF41]/80">
                                                                    <Camera className="w-3 h-3" />
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 text-xs text-white/60">
                                                            {r.investigation_type && <span>{r.investigation_type}</span>}
                                                            {r.created_at && (
                                                                <span className="flex items-center gap-1">
                                                                    <Calendar className="w-3 h-3" />
                                                                    {formatDateIT(r.created_at)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {isSelected && <CheckCircle2 className="w-5 h-5 text-[#00FF41] flex-shrink-0" />}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* Opzioni import */}
                    {!importResult && selected && (
                        <div className="mt-4 p-4 border border-white/10 bg-black/40 space-y-3" data-testid="import-onboarding-options">
                            <p className="text-white text-sm font-medium">Opzioni import</p>
                            {caseHasInvestigatedData && (
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={overwrite}
                                        onChange={(e) => setOverwrite(e.target.checked)}
                                        className="mt-0.5 accent-[#00FF41]"
                                        data-testid="import-onboarding-overwrite-checkbox"
                                    />
                                    <span className="text-sm text-white/80">
                                        Sovrascrivi i campi indagato già compilati
                                        <span className="block text-xs text-white/40">Se disattivato, i campi esistenti vengono preservati</span>
                                    </span>
                                </label>
                            )}
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={includePhotos}
                                    onChange={(e) => setIncludePhotos(e.target.checked)}
                                    className="mt-0.5 accent-[#00FF41]"
                                    data-testid="import-onboarding-photos-checkbox"
                                />
                                <span className="text-sm text-white/80">
                                    Importa anche le foto dell'indagato
                                    {selected.has_photos ? (
                                        <span className="block text-xs text-[#00FF41]/70">Foto disponibili su Onboarding</span>
                                    ) : (
                                        <span className="block text-xs text-white/40">Nessuna foto disponibile</span>
                                    )}
                                </span>
                            </label>
                        </div>
                    )}

                    {importError && (
                        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="import-onboarding-error">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{importError}</span>
                        </div>
                    )}

                    {importResult && (
                        <div className="p-4 bg-[#00FF41]/10 border border-[#00FF41]/40 text-sm" data-testid="import-onboarding-success">
                            <div className="flex items-start gap-2 text-[#00FF41] font-medium mb-2">
                                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                                <span>Import completato</span>
                            </div>
                            <p className="text-white/80">{importResult.message}</p>
                            {importResult.imported_fields?.length > 0 && (
                                <ul className="mt-2 text-xs text-white/60 list-disc list-inside">
                                    {importResult.imported_fields.map((f) => (
                                        <li key={f}>{f}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 flex items-center justify-end gap-2">
                    {importResult ? (
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-[#00FF41] text-black text-sm font-mono uppercase hover:bg-[#00FF41]/80"
                            data-testid="import-onboarding-done-btn"
                        >
                            Chiudi
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={handleClose}
                                disabled={importing}
                                className="px-4 py-2 text-white/70 text-sm font-mono uppercase hover:text-white disabled:opacity-50"
                                data-testid="import-onboarding-cancel-btn"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={!selected || importing}
                                className="px-4 py-2 bg-[#00FF41] text-black text-sm font-mono uppercase hover:bg-[#00FF41]/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                data-testid="import-onboarding-confirm-btn"
                            >
                                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                Importa nel caso
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
